import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type ConsentPurpose =
  | "data_processing"
  | "marketing_email"
  | "marketing_sms"
  | "analytics"
  | "third_party_sharing";

export interface ConsentRecord {
  id: string;
  tenant_id: string;
  student_id: string;
  student_name: string | null;
  purpose: ConsentPurpose;
  granted: boolean;
  granted_at: string;
  withdrawn_at: string | null;
  note: string | null;
  recorded_by: string | null;
}

export interface ConsentListResponse {
  items: ConsentRecord[];
  total: number;
}

export interface UpsertConsentBody {
  student_id: string;
  purpose: ConsentPurpose;
  granted: boolean;
  note?: string | null;
}

export const CONSENT_PURPOSE_LABELS: Record<ConsentPurpose, string> = {
  data_processing: "Data processing",
  marketing_email: "Marketing email",
  marketing_sms: "Marketing SMS",
  analytics: "Analytics",
  third_party_sharing: "Third-party sharing",
};

export function useConsentRecords(params?: {
  student_id?: string;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.student_id) qs.set("student_id", params.student_id);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useQuery<ConsentListResponse>({
    queryKey: ["dpdp-consents", params],
    queryFn: () => api.get<ConsentListResponse>(`/api/dpdp/consents${suffix}`),
  });
}

export function useUpsertConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertConsentBody) => api.put<ConsentRecord>("/api/dpdp/consents", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dpdp-consents"] });
    },
  });
}
